<template>
  <div class="gsl-hrm-card-container">
    <oxd-text tag="h6" class="gsl-hrm-main-title">
      {{ $t('general.project_allocation') }}
    </oxd-text>

    <oxd-divider />

    <oxd-form :loading="isLoading" @submit-valid="onSave">
      <oxd-form-row>
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="allocation.fromDate"
              :label="$t('general.from_date')"
              :rules="rules.fromDate"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="allocation.toDate"
              :label="$t('general.to_date')"
              :rules="rules.toDate"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <project-autocomplete
              :label="$t('time.project_name')"
              :rules="rules.project"
              :model-value="getProject(allocation.project)"
              @update:model-value="updateProject($event)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="allocation.allocatedPercentage"
              :label="$t('general.percentage_of_allocation')"
              :rules="rules.allocatedPercentage"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row v-if="isRoleRequired">
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <job-role-dropdown
              v-tooltip="$t('general.roles_available_for_this_project')"
              :label="$t('general.role')"
              :rules="isRoleRequired ? rules.role : []"
              :model-value="getRole(allocation.role)"
              :params="projectParams"
              @update:model-value="updateRole($event)"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <div v-if="allocation.project.id" class="gsl-hrm-footer">
        <oxd-text tag="p" class="gsl-hrm-footer-link-title">
          <oxd-text
            tag="span"
            class="gsl-hrm-footer-link"
            @click="onClickProjectBudget()"
          >
            Edit Project Budget to make available of roles
          </oxd-text>
        </oxd-text>
      </div>
      <oxd-divider />
      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <submit-button />
      </oxd-form-actions>
    </oxd-form>
  </div>
</template>
<script>
import ProjectAutocomplete from '@/orangehrmTimePlugin/components/ProjectAutocomplete.vue';
import {
  required,
  validSelection,
  validDateFormat,
  startDateShouldBeBeforeEndDate,
  endDateShouldBeAfterStartDate,
} from '@ohrm/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';
import JobRoleDropdown from '@/orangehrmPimPlugin/components/JobRoleDropdown.vue';
import {urlFor} from '@ohrm/core/util/helper/url';
import infoTooltipDirective from '@/core/util/directives/infoTooltip';

const jobProjectAllocationModel = {
  fromDate: '',
  toDate: '',
  project: '',
  allocatedPercentage: '',
  role: null,
};

export default {
  name: 'SaveProjectAllocation',
  components: {
    'project-autocomplete': ProjectAutocomplete,
    'job-role-dropdown': JobRoleDropdown,
  },
  directives: {
    tooltip: infoTooltipDirective,
  },
  props: {
    http: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  setup() {
    const {userDateFormat} = useDateFormat();

    return {
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      allocation: {...jobProjectAllocationModel},
      isRoleRequired: false,
      rules: {
        fromDate: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.allocation.toDate,
            this.$t('general.from_date_should_be_before_to_date'),
            {allowSameDate: true},
          ),
        ],
        toDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.allocation.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
        project: [
          required,
          validSelection,
          (v) => {
            return new Promise((resolve) => {
              this.http.getAll().then((res) => {
                const {data} = res.data;

                if (!this.allocation.fromDate || !this.allocation.toDate) {
                  return resolve(this.$t('general.dates_are_required'));
                }
                if (this.allocation.toDate && v.endDate) {
                  if (new Date(this.allocation.toDate) > new Date(v.endDate)) {
                    return resolve(
                      this.$t('general.this_project_will_end_on_x', {
                        date: v.endDate,
                      }),
                    );
                  }
                }
                const newFromDate = new Date(this.allocation.fromDate);
                const newToDate = new Date(this.allocation.toDate);

                const isProjectAlreadyChosen = data.some((item) => {
                  const existingFromDate = new Date(item.startDate);
                  const existingToDate = new Date(item.endDate);

                  const isSameProject = item.project.id === v.id;

                  const isDateOverlapping =
                    (newFromDate >= existingFromDate &&
                      newFromDate <= existingToDate) ||
                    (newToDate >= existingFromDate &&
                      newToDate <= existingToDate) ||
                    (newFromDate <= existingFromDate &&
                      newToDate >= existingToDate);

                  return isSameProject && isDateOverlapping;
                });
                return isProjectAlreadyChosen
                  ? resolve(this.$t('general.project_is_already_allocated'))
                  : resolve(true);
              });
            });
          },
        ],
        allocatedPercentage: [
          required,
          (v) => {
            return new Promise((resolve) => {
              this.http.getAll().then((res) => {
                const {data} = res.data;

                if (!this.allocation.fromDate || !this.allocation.toDate) {
                  return resolve(this.$t('general.dates_are_required'));
                }
                const newFromDate = new Date(this.allocation.fromDate);
                const newToDate = new Date(this.allocation.toDate);

                let overlappingPercentage = 0;

                data.forEach((item) => {
                  const existingFromDate = new Date(item.startDate);
                  const existingToDate = new Date(item.endDate);

                  // Check if the date ranges overlap
                  const isDateOverlapping =
                    (newFromDate >= existingFromDate &&
                      newFromDate <= existingToDate) ||
                    (newToDate >= existingFromDate &&
                      newToDate <= existingToDate) ||
                    (newFromDate <= existingFromDate &&
                      newToDate >= existingToDate);

                  if (isDateOverlapping) {
                    overlappingPercentage += item.percentage;
                  }
                });

                const totalPercentage = overlappingPercentage + parseInt(v, 10);
                if (totalPercentage > 100) {
                  return resolve(
                    this.$t('general.percentage_is_only_n_left_to_allocate', {
                      value: 100 - overlappingPercentage,
                    }),
                  );
                } else {
                  return resolve(true);
                }
              });
            });
          },
        ],
        role: [
          required,
          (v) => {
            return new Promise((resolve) => {
              if (!this.allocation.project.id) {
                return resolve(this.$t('general.project_has_to_be_selected'));
              }
              this.http
                .request({
                  method: 'GET',
                  url: `/api/v2/time/project/${this.allocation.project.id}/project-allocation`,
                })
                .then((res) => {
                  const {data} = res.data;

                  if (!this.allocation.fromDate || !this.allocation.toDate) {
                    return resolve(this.$t('general.dates_are_required'));
                  }
                  const currentDate = new Date(this.allocation.fromDate);

                  let countOfRoles = 0;
                  data.forEach((item) => {
                    const endDate = new Date(item.endDate);
                    // Determine the status based on the endDate
                    if (v.id === item.role.id) {
                      const usedRole = endDate < currentDate ? 0 : 1;
                      countOfRoles = countOfRoles + usedRole;
                    }
                  });
                  if (countOfRoles + 1 > v.headCount) {
                    return resolve(
                      this.$t('general.max_role_has_been_assigned'),
                    );
                  } else {
                    return resolve(true);
                  }
                });
            });
          },
        ],
      },
    };
  },
  computed: {
    projectParams() {
      return {
        projectId: this.allocation.project?.id || null,
      };
    },
  },
  methods: {
    onClickProjectBudget() {
      window.open(
        urlFor(`/time/saveProject/${this.allocation?.project?.id}`),
        '_blank',
      );
    },
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          fromDate: this.allocation.fromDate,
          toDate: this.allocation.toDate,
          projectId: this.allocation.project.id,
          allocatedPercentage: this.allocation.allocatedPercentage,
          roleId: this.allocation?.role?.id,
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.allocation = {...jobProjectAllocationModel};
          this.onCancel();
        });
    },
    getProject(project) {
      if (project) {
        return {
          id: project?.id,
          label: project?.label
            ? project?.label
            : project?.customerName + ' - ' + project?.projectName,
          startDate: project?.startDate,
          endDate: project?.endDate,
          budget: project?.budget,
        };
      } else {
        return null;
      }
    },
    getRole(role) {
      if (role && role.label) {
        return role;
      }
      if (role?.id) {
        return {
          id: role?.id,
          label: `${role?.name}`,
          headCount: role?.headCount,
        };
      }
      return null;
    },
    updateProject($value) {
      this.allocation.project = $value;
      this.isRoleRequired = $value?.budget === true;
    },
    updateRole($value) {
      this.allocation.role = $value;
    },
    onCancel() {
      this.$emit('close', true);
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-footer-link-title {
  font-size: 13px;
  margin-right: 10px;
}
.gsl-hrm-footer-link {
  color: $oxd-primary-one-color;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
</style>
